import { type QueryKey } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { buildsGridConfig } from '@/features/builds/grid/gridConfig';
import { useDeleteBuild, useUpdateBuild } from '@/features/builds/hooks/builds';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter, useProjectMetricThresholds } from '@/features/projects';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { buildTagColorByName } from '@/features/shared/tags';
import { useNotifications } from '@/providers/notificationHooks';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { useTagOptions } from '@/shared/hooks';
import { useIsAuthenticated } from '@/shared/hooks/auth';
import { buildBuildsColumns } from './buildsColumns';
import type { BuildsTableProps } from '@/features/builds/components/BuildsTable.types';
import type { DetailEditEntityDialogProps } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColDef } from '@/ui/primitives';
import type { BuildDto, QueryResult } from '@flowqa/shared';

type BuildsTableControllerInput = Pick<
  BuildsTableProps,
  | 'fixedFilters'
  | 'initialFilterModel'
  | 'initialSortModel'
  | 'nameColumnHeaderName'
  | 'nameColumnMode'
  | 'persistKey'
  | 'queryFn'
  | 'queryKey'
  | 'runHrefBuilder'
  | 'showJobColumn'
  | 'showPipelineColumn'
  | 'showStageSummaryColumn'
  | 'syncStateToUrl'
  | 'tagOptionsOverride'
>;

type BuildEditDialogSaveHandler = NonNullable<
  DetailEditEntityDialogProps<BuildDto['status']>['onSave']
>;

type BuildsTableController = {
  columns: Array<GridColDef<BuildDto>>;
  deleteBuildPending: boolean;
  deleteTarget: BuildDto | null;
  editEditor: ReturnType<typeof useDetailEditEditor<BuildDto['status']>>;
  editTarget: BuildDto | null;
  editTargetDurationMs: number | null;
  editTargetStartedAt: Date | null;
  gridConfig: ReturnType<typeof buildsGridConfig>;
  handleCloseDeleteDialog: () => void;
  handleCloseEditDialog: () => void;
  handleConfirmDelete: () => void;
  handleSaveEditDialog: BuildEditDialogSaveHandler;
  isAuthenticated: boolean;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<BuildDto>>;
  queryKey: QueryKey;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  togglableColumnFields: string[];
};

export const useBuildsTableController = ({
  fixedFilters,
  initialFilterModel,
  initialSortModel,
  nameColumnHeaderName,
  nameColumnMode = 'pipeline',
  persistKey,
  queryFn,
  queryKey,
  runHrefBuilder,
  showJobColumn = false,
  showPipelineColumn = true,
  showStageSummaryColumn = true,
  syncStateToUrl = false,
  tagOptionsOverride,
}: BuildsTableControllerInput): BuildsTableController => {
  useAuth();
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const { tagOptions: fetchedTagOptions } = useTagOptions({
    projectId: selectedProjectId,
    enabled: tagOptionsOverride === undefined,
  });
  const tagOptions = tagOptionsOverride ?? fetchedTagOptions;
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const updateBuild = useUpdateBuild();
  const deleteBuild = useDeleteBuild();
  const [editTarget, setEditTarget] = useState<BuildDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BuildDto | null>(null);
  const editEditor = useDetailEditEditor<BuildDto['status']>({
    projectId: editTarget?.projectId ?? null,
    successMessage: 'Job updated',
    saveEntity: async ({ tags, status, timestamp, durationMs }) => {
      if (!editTarget) return;
      const startedAt = timestamp ? timestamp.toISOString() : null;
      const finishedAt =
        timestamp && durationMs !== null && durationMs !== undefined
          ? new Date(timestamp.getTime() + durationMs).toISOString()
          : null;
      await updateBuild.mutateAsync({
        id: editTarget.id,
        tags,
        status,
        startedAt,
        finishedAt,
      });
    },
  });
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const columns: Array<GridColDef<BuildDto>> = useMemo(
    () =>
      buildBuildsColumns({
        nameColumnMode,
        nameColumnHeaderName,
        showStageSummaryColumn,
        showJobColumn,
        showPipelineColumn,
        durationThreshold: thresholds.pipelines.avgDuration,
        runHrefBuilder,
        tagColorByName,
        includeActionsColumn: true,
        onEditRow: setEditTarget,
        onDeleteRow: setDeleteTarget,
        editRowPendingId: editEditor.isPending ? (editTarget?.id ?? null) : null,
        deleteRowPendingId: deleteBuild.isPending ? (deleteTarget?.id ?? null) : null,
      }),
    [
      deleteBuild.isPending,
      deleteTarget?.id,
      editEditor.isPending,
      editTarget?.id,
      nameColumnHeaderName,
      nameColumnMode,
      runHrefBuilder,
      showJobColumn,
      showPipelineColumn,
      showStageSummaryColumn,
      tagColorByName,
      thresholds.pipelines.avgDuration,
    ],
  );
  const togglableColumnFields = useMemo(() => columns.map((column) => column.field), [columns]);
  const gridConfig = buildsGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey === undefined ? 'builds-table' : persistKey,
    fixedFilters,
    initialSortModel,
    initialFilterModel,
  });
  const editTargetStartedAt = editTarget?.startedAt ?? null;
  const editTargetFinishedAt = editTarget?.finishedAt ?? null;
  const editTargetDurationMs =
    !editTargetStartedAt || !editTargetFinishedAt
      ? null
      : Math.max(editTargetFinishedAt.getTime() - editTargetStartedAt.getTime(), 0);
  const handleCloseEditDialog = useCallback(() => {
    if (editEditor.isPending) return;
    setEditTarget(null);
  }, [editEditor.isPending]);
  const handleSaveEditDialog = useCallback<BuildEditDialogSaveHandler>(
    async ({ tags, colors, status, timestamp, durationMs }) => {
      await editEditor.save({ tags, colors, status, timestamp, durationMs });
      setEditTarget(null);
    },
    [editEditor],
  );
  const handleCloseDeleteDialog = useCallback(() => {
    if (deleteBuild.isPending) return;
    setDeleteTarget(null);
  }, [deleteBuild.isPending]);
  const handleConfirmDelete = useCallback(() => {
    if (!deleteTarget) return;
    const deletedBuildName = deleteTarget.buildKey ?? deleteTarget.id;
    deleteBuild.mutate(
      { id: deleteTarget.id, projectId: deleteTarget.projectId },
      {
        onSuccess: () => {
          notify({
            message: getToastMessage('entity.deleted', { entity: 'job', name: deletedBuildName }),
            tone: 'green',
          });
          setDeleteTarget(null);
        },
        onError: (error) => {
          notify({ message: toErrorMessage(error), tone: 'red' });
        },
      },
    );
  }, [deleteBuild, deleteTarget, notify, toErrorMessage]);

  return {
    columns,
    deleteBuildPending: deleteBuild.isPending,
    deleteTarget,
    editEditor,
    editTarget,
    editTargetDurationMs,
    editTargetStartedAt,
    gridConfig,
    handleCloseDeleteDialog,
    handleCloseEditDialog,
    handleConfirmDelete,
    handleSaveEditDialog,
    isAuthenticated,
    queryFn: queryFn ?? gridConfig.queryFn,
    queryKey: queryKey ?? gridConfig.queryKey,
    selectedOrganisationId,
    selectedProjectId,
    togglableColumnFields,
  };
};
